import { PortfolioAndBenchmarkPerfDataDtoV3 } from "../../../../../../generated-api-v3";
import { ITimePeriod } from "../../../PeriodChooser";
import { useSmallScreen } from "@iliotech/data-wire/src/reactHooks/hooks/useSmallScreen";
import moment, { MomentInput } from "moment";
import {
  Chart,
  ChartArea,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  ChartTooltip,
  ChartValueAxis,
  ChartValueAxisItem,
  SharedTooltipContext,
} from "@progress/kendo-react-charts";
import React from "react";
import cn from "classnames";
import s from "./PNLLineChart.module.scss";
import { Empty } from "@iliotech/widgets";

// if it is the last week pick the last value
export const aggregateByLastDate =
  (lastDate: MomentInput) => (values: any, series: any, dataItems: any) => {
    const found = dataItems.find((item: any) =>
      moment(item.date).isSame(moment(lastDate), "day")
    );
    if (found) {
      return found.value;
    }

    return dataItems.sort((a: any, b: any) =>
      moment(a.date).diff(moment(b.date))
    )[0]?.value;
  };

interface IValueChartProps {
  currencyFormatter(value: number | undefined, decimal?: number): string;
  pnlPerformance?: PortfolioAndBenchmarkPerfDataDtoV3;
  periodDays: number;
  generating?: boolean;
  period?: ITimePeriod;
  height?: number;
  width?: number;
}

const BULLET_SIZE = 18;

export const ValueChart = ({
  currencyFormatter,
  period,
  pnlPerformance,
  periodDays,
  generating = false,
  height,
  width,
}: // lineData = [],
IValueChartProps) => {
  const isMobile = useSmallScreen();

  const linesMap: {
    [key: string]: {
      color: string;
      name: string;
      data: { date: string; value: number; type: string }[];
    };
  } = {
    nav: {
      data: [],
      color: "#008001",
      name: "Net Worth",
    },
    totalInv: {
      data: [],
      color: "#FFAF33",
      name: "Net investment over the selected period",
    },
  };

  const dates: string[] = [];

  const lastDate = pnlPerformance?.portfolioData
    .map((item) => item.date)
    .sort((a, b) => moment(b).diff(moment(a)))[0];

  const filteredData = pnlPerformance?.portfolioData;

  filteredData?.forEach((row) => {
    dates.push(row.date);
    linesMap.nav.data.push({
      date: row.date,
      value: (row as any).nav,
      type: "nav",
    });
    linesMap.totalInv.data.push({
      date: row.date,
      value: (row as any).totalInv,
      type: "totalInv",
    });
  });

  const lines = Object.values(linesMap);

  const sharedTooltipRender = (context: SharedTooltipContext) => {
    let nav: number | undefined = undefined;
    let totalInv: number | undefined = undefined;

    const points = context.points
      ?.map((point, i) => {
        // const line = lines[point.series.index];
        if (point.series.name === linesMap.nav.name) {
          nav = point.value;
        }
        if (point.series.name === linesMap.totalInv.name) {
          totalInv = point.value;
        }
        return {
          label: point.series.name,
          color: point.series.color,
          value: point.value,
          series: point.dataItem,
        };
      })
      .sort((a, b) => b.value - a.value);

    const showPnl =
      typeof nav !== "undefined" && typeof totalInv !== "undefined";

    // return (
    //   <pre style={{ fontSize: 12 }}>{JSON.stringify({ points }, null, 2)}</pre>
    // );

    return (
      <div className={cn(s.tooltipSurround)}>
        <ul>
          {points?.map((point) => (
            <li key={point.label}>
              <div className={cn(s.row)}>
                <div
                  style={{
                    width: BULLET_SIZE,
                    height: BULLET_SIZE,
                    borderRadius: BULLET_SIZE / 2,
                    background: point.color,
                    marginRight: 5,
                  }}
                />
                <div className={cn(s.row, s.details)}>
                  <div className={cn(s.label)}>{point.label}:&nbsp;&nbsp;</div>
                  <div className={cn(s.value)}>
                    {currencyFormatter(point.value)}
                  </div>
                </div>
              </div>
            </li>
          ))}

          {showPnl && (
            <li>
              <div className={cn(s.row)}>
                <div
                  style={{
                    width: BULLET_SIZE,
                    height: BULLET_SIZE,
                    borderRadius: BULLET_SIZE / 2,
                    background: "transparent",
                    marginRight: 5,
                  }}
                />
                <div className={cn(s.row, s.details)}>
                  <div className={cn(s.label)}>
                    P&L over the selected period:&nbsp;&nbsp;
                  </div>
                  <div className={cn(s.value)}>
                    {currencyFormatter(nav! - totalInv!)}
                  </div>
                </div>
              </div>
            </li>
          )}
        </ul>
      </div>
    );
  };

  if (periodDays < 7) {
    return (
      <Empty text={"Select at least 1 weeks' worth of data to view by Value"} />
    );
  }
  return (
    <Chart style={{ height: height, width: width }}>
      <ChartArea background={"transparent"} height={height} width={width} />
      <ChartLegend visible={false} />
      <ChartTooltip
        opacity={0.9}
        background={"var(--dark)"}
        shared={true}
        render={sharedTooltipRender as any}
      />

      <ChartSeries>
        {Object.entries(linesMap).map(([, stat]) => {
          return (
            <ChartSeriesItem
              data={stat.data}
              key={stat.name}
              markers={{ visible: false }}
              name={stat.name}
              aggregate={aggregateByLastDate(lastDate)}
              // markers={{...series.markers, border: {color: series.color}}}
              field={"value"}
              color={stat.color}
              categoryField={"date"}
              type={"area"}
              width={2}
            />
          );
        })}
      </ChartSeries>
      <ChartValueAxis>
        <ChartValueAxisItem
          majorGridLines={{
            color: generating ? undefined : "var(--color-lighter)",
          }}
          labels={{
            content: (e) => currencyFormatter(e.value),
            color: "var(--color)",
          }}
        />
      </ChartValueAxis>
      <ChartCategoryAxis>
        <ChartCategoryAxisItem
          type={"date"}
          baseUnit={"weeks"}
          pane={"bottom"}
          maxDivisions={20}
          majorGridLines={{ visible: false }}
          minorGridLines={{ visible: false }}
          labels={{
            color: "var(--color)",
            rotation: -45,
            font: `12px "DM Sans", Nunito, sans-serif`,
            position: "start",
            content: (e) => moment(e.value).format(`DD MMM 'YY`),
          }}
        />
      </ChartCategoryAxis>
    </Chart>
  );
};
